import { useState } from "react";
import { Trash2, Pencil } from "lucide-react";
import { api } from "../lib/api";
import { MEALS, fdate, fmt, num, run, useLoad, weekday } from "../lib/util";
import { Alert, Btn, Card, Field, Input, ProductSelect, Select, SectionTitle, Table, Empty } from "../components/kit";
import { useSchool } from "./ctx";

function WithdrawalForm({ initial, onSaved, onCancel }) {
  const { products, month } = useSchool();
  const [f, setF] = useState({ date: `${month}-01`, product_id: null, withdrawn: "", returned: "", notes: "", ...initial });
  const save = async () => {
    const body = { date: f.date, product_id: f.product_id, withdrawn: num(f.withdrawn) ?? 0, returned: num(f.returned) ?? 0, notes: f.notes || null, version: f.version ?? null };
    if (await run(() => (f.id ? api.put(`/s/withdrawals/${f.id}`, body) : api.post("/s/withdrawals", body)), "Retirada registrada")) {
      onSaved();
      setF({ ...f, id: undefined, version: undefined, withdrawn: "", returned: "", notes: "" });
    }
  };
  const net = (num(f.withdrawn) || 0) - (num(f.returned) || 0);
  return (
    <Card>
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
        <Field label="Data"><Input data-testid="wd-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Produto" className="lg:col-span-2"><ProductSelect testid="wd-product-select" products={products} value={f.product_id} onChange={(id) => setF({ ...f, product_id: id })} /></Field>
        <Field label="Qtd. retirada"><Input data-testid="wd-withdrawn-input" type="number" step="any" value={f.withdrawn} onChange={(e) => setF({ ...f, withdrawn: e.target.value })} /></Field>
        <Field label="Devolvida sem uso"><Input data-testid="wd-returned-input" type="number" step="any" value={f.returned} onChange={(e) => setF({ ...f, returned: e.target.value })} /></Field>
        <div className="text-sm">Saída líquida: <b data-testid="wd-net">{fmt(net)}</b></div>
        <Field label="Observações" className="sm:col-span-2 lg:col-span-4"><Input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <div className="flex gap-2 lg:col-span-2">
          <Btn data-testid="wd-save-button" onClick={save}>{f.id ? "Salvar edição" : "Registrar retirada"}</Btn>
          {f.id && <Btn variant="outline" onClick={onCancel}>Cancelar</Btn>}
        </div>
      </div>
    </Card>
  );
}

function RealComensais() {
  const { month, tick } = useSchool();
  const [rows, reload] = useLoad(async () => (await api.get("/s/real-comensais", { params: { month } })).data, [month, tick]);
  const [f, setF] = useState({ date: `${month}-01`, meal: "almoco", count: "" });
  const add = async () => {
    if (await run(() => api.post("/s/real-comensais", { date: f.date, meal: f.meal, count: parseInt(f.count, 10) }), "Comensais reais registrados")) { reload(); setF({ ...f, count: "" }); }
  };
  const del = async (r) => (await run(() => api.delete(`/s/real-comensais/${r.id}`))) && reload();
  return (
    <div className="space-y-3">
      <h3 className="font-heading font-bold text-[#0B192C] text-lg">Comensais reais por dia e refeição</h3>
      <Card className="flex flex-wrap gap-3 items-end">
        <Field label="Data"><Input data-testid="rc-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Refeição"><Select data-testid="rc-meal-select" value={f.meal} onChange={(e) => setF({ ...f, meal: e.target.value })} options={Object.entries(MEALS)} /></Field>
        <Field label="Comensais"><Input data-testid="rc-count-input" type="number" min="0" value={f.count} onChange={(e) => setF({ ...f, count: e.target.value })} /></Field>
        <Btn data-testid="rc-save-button" onClick={add}>Registrar</Btn>
      </Card>
      <Table testid="real-comensais-table">
        <thead><tr><th>Data</th><th>Refeição</th><th className="num">Comensais</th><th /></tr></thead>
        <tbody>{(rows || []).map((r) => <tr key={r.id}><td>{fdate(r.date)}</td><td>{MEALS[r.meal]}</td><td className="num">{r.count}</td><td><button onClick={() => del(r)}><Trash2 className="w-4 h-4 text-red-500" /></button></td></tr>)}</tbody>
      </Table>
    </div>
  );
}

export default function Withdrawals() {
  const { month, tick, bump, products } = useSchool();
  const [rows, reload] = useLoad(async () => (await api.get("/s/withdrawals", { params: { month } })).data, [month, tick]);
  const [edit, setEdit] = useState(null);
  const pmap = Object.fromEntries(products.map((p) => [p.id, p]));
  const del = async (r) => {
    if (window.confirm("Remover esta retirada?") && (await run(() => api.delete(`/s/withdrawals/${r.id}`), "Removida"))) { reload(); bump(); }
  };
  const weeks = [...new Set((rows || []).map((r) => r.week))].sort();
  return (
    <div className="space-y-5">
      <SectionTitle title="Retiradas semanais — modo B (Retiradas)" subtitle="Quantidades efetivamente retiradas do estoque para a merenda. Saída líquida = retirada − devolução. Não representa medição do consumo ingerido." />
      <Alert tone="info">As retiradas só entram nos totais do mês quando a base “B — Retiradas” estiver selecionada no topo. Os registros do Modo IA e das retiradas são preservados ao alternar.</Alert>
      <WithdrawalForm key={edit?.id || "new"} initial={edit || {}} onCancel={() => setEdit(null)} onSaved={() => { reload(); bump(); setEdit(null); }} />
      {weeks.map((w) => (
        <div key={w}>
          <h4 className="font-semibold text-sm text-slate-600 mb-2">Semana {w} do mês</h4>
          <Table testid={`withdrawals-week-${w}`}>
            <thead><tr><th>Data</th><th>Produto</th><th className="num">Retirada</th><th className="num">Devolvida</th><th className="num">Saída líquida</th><th>Observações</th><th /></tr></thead>
            <tbody>
              {rows.filter((r) => r.week === w).map((r) => (
                <tr key={r.id}>
                  <td>{fdate(r.date)} <span className="text-xs text-slate-400">{weekday(r.date)}</span></td>
                  <td>{pmap[r.product_id]?.name} <span className="text-xs text-slate-400">{pmap[r.product_id]?.unit}</span></td>
                  <td className="num">{fmt(r.withdrawn)}</td>
                  <td className="num">{fmt(r.returned)}</td>
                  <td className="num font-bold">{fmt(r.net)}</td>
                  <td className="text-xs">{r.notes}</td>
                  <td className="whitespace-nowrap">
                    <Btn variant="ghost" onClick={() => setEdit(r)}><Pencil className="w-4 h-4" /></Btn>
                    <Btn variant="ghost" onClick={() => del(r)}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ))}
      {rows && !rows.length && <Empty>Nenhuma retirada registrada neste mês.</Empty>}
      <RealComensais />
    </div>
  );
}
